// frontend/src/components/Trades.tsx
import { useState, useEffect, type ReactNode } from "react";
import type { Key } from "react-aria-components";
import {
  Alert,
  Button,
  Chip,
  Label,
  ListBox,
  Meter,
  Modal,
  NumberField,
  Select,
  Skeleton,
  Table,
  Tabs,
} from "@heroui/react";
import { Bot, History, Inbox, MessageCircleQuestion, ShieldCheck, X } from "lucide-react";
import { DirectionChip, EmptyState, Panel, Pnl, Segmented } from "@/components/kit";
import { cn } from "@/lib/utils";
import { usePriceStream } from "../hooks/usePriceStream";
import { formatR } from "./chart/chartSignals";
import { closedTradeR, type TrackedTrade, type TradeSource, type TradeStats } from "./desk/deskData";
import { ChallengePanel } from "./desk/ChallengePanel";
import { useDayRiskStatus } from "../hooks/useDayRisk";
import { useSettings } from "../hooks/useSettings";
import { TradeReviewLoader, parseReview, type TradeReviewData } from "./trades/TradeReview";

type SourceFilter = "all" | TradeSource;
const SOURCE_FILTERS = [
  { id: "all", label: "All" },
  { id: "auto", label: "Engine signals" },
  { id: "manual", label: "Yours" },
] as const;

const bySourceFilter = <T extends { source?: string | null }>(rows: T[], source: SourceFilter) =>
  source === "all" ? rows : rows.filter((t) => (t.source === "auto" ? "auto" : "manual") === source);

/** What an empty tab means for the chosen filter. */
const EMPTY_OPEN: Record<SourceFilter, { title: string; description: string }> = {
  all: { title: "No open trades", description: "Your trades and the engine's signals show up here while they're open." },
  auto: {
    title: "No open engine signals right now",
    description: "The engine sends only a few BUY or SELL signals a week. Each one shows up here by itself while it's open, as a practice trade.",
  },
  manual: { title: "None of your trades are open", description: "Log a trade with Buy or Sell on the desk, real or practice, and it shows up here." },
};
const EMPTY_HISTORY: Record<SourceFilter, { title: string; description: string }> = {
  all: { title: "No closed trades yet", description: "Closed trades, their result and why they won or lost are listed here." },
  auto: {
    title: "No engine signal has closed yet",
    description: "Each signal the engine sends is listed here once it hits its stop, a target or its time limit.",
  },
  manual: { title: "You haven't closed a trade yet", description: "Your closed trades, their result and why they won or lost are listed here." },
};

interface OpenTrade {
  id: string;
  symbol: string;
  direction: "LONG" | "SHORT";
  entry_price: number;
  stop_loss: number;
  take_profit_1: number;
  margin?: number;
  status?: string;
  /** "paper" = practice trade. */
  mode?: string | null;
  /** "auto" = an engine signal tracked automatically. */
  source?: string | null;
  /** The daily limit this real trade broke when it was logged (desk LOCKED). */
  rule_break?: string | null;
  /** 0.5 = sized at half risk (an Against-BTC signal). */
  risk_scale?: number | null;
}

interface ClosedTrade {
  id: string;
  symbol: string;
  side?: string;
  direction?: "LONG" | "SHORT";
  entryPrice?: number;
  entry?: number;
  exitPrice?: number;
  exit?: number;
  pnl?: number;
  exitDate?: string;
  closedAt?: string;
  mode?: string | null;
  /** Net R from the tracker (after fees). */
  r?: number | null;
  source?: string | null;
  /** Why it won or lost (stored once the backend has reviewed it). */
  review?: TradeReviewData | null;
  /** The daily limit this real trade broke when it was logged (desk LOCKED). */
  rule_break?: string | null;
  /** 0.5 = sized at half risk (an Against-BTC signal). */
  risk_scale?: number | null;
}

/** Tracked-trade API row → history row. */
function toClosed(t: any): ClosedTrade {
  return {
    id: t.id,
    symbol: t.symbol,
    direction: t.direction,
    entryPrice: t.entry_price,
    exitPrice: t.exit_price,
    pnl: t.pnl,
    closedAt: t.closed_at ? new Date(t.closed_at).toISOString() : undefined,
    mode: t.mode,
    r: closedTradeR(t),
    source: t.source,
    review: parseReview(t.review),
    rule_break: t.rule_break,
    risk_scale: t.risk_scale,
  };
}

/** The engine's signals that went with BTC vs against it (half risk), tracked apart. */
type ByBtc = { with: TradeStats; against: TradeStats };
const isHalf = (t: { risk_scale?: number | null }) => (t.risk_scale ?? 1) < 1;

type CloseForm = { tradeId: string; exitPrice: string; reason: string };

const CLOSE_REASONS = ["Hit TP1", "Hit TP2", "Hit Stop", "Manual"] as const;

const fmtPrice = (v: number) => `$${v.toLocaleString()}`;

function HeatGauge({ heat, cap = 5 }: { heat: number; cap?: number }) {
  const color = heat > cap ? "danger" : heat > cap * 0.8 ? "warning" : "accent";
  return (
    <Meter
      aria-label="Portfolio heat"
      value={Math.min(heat, cap)}
      minValue={0}
      maxValue={cap}
      size="sm"
      color={color}
      className="w-full sm:w-56"
    >
      <div className="flex items-center justify-between gap-3 text-2xs">
        <Label className="text-2xs font-medium uppercase tracking-wide text-muted">Portfolio heat</Label>
        <span className={cn("num", heat > cap ? "text-danger" : "text-foreground")}>
          {heat.toFixed(1)}% / {cap}%
        </span>
      </div>
      <Meter.Track>
        <Meter.Fill />
      </Meter.Track>
    </Meter>
  );
}

export function Trades() {
  const { settings } = useSettings();
  const [tab, setTab] = useState<"open" | "history">("open");
  const [openTrades, setOpenTrades] = useState<OpenTrade[]>([]);
  const [history, setHistory] = useState<ClosedTrade[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [closing, setClosing] = useState<string | null>(null);
  const [closeForm, setCloseForm] = useState<CloseForm | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);
  const [portfolioHeat, setPortfolioHeat] = useState(0);
  const [record, setRecord] = useState<Record<TradeSource, TradeStats> | null>(null);
  const [byBtc, setByBtc] = useState<ByBtc | null>(null);
  const [source, setSource] = useState<SourceFilter>("all");
  const [reviewing, setReviewing] = useState<{ id: string; symbol: string } | null>(null);
  // Every trade as the API lists it: the 30-day plan counts yours day by day.
  const [allTrades, setAllTrades] = useState<TrackedTrade[]>([]);
  const dayRisk = useDayRiskStatus();

  const showToast = (msg: string, type: "success" | "error") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [tradesRes, heatRes, summaryRes] = await Promise.all([
          fetch(`/api/tracked-trades`),
          fetch(`/api/portfolio/heat`).catch(() => null), // account size: the desk's one risk setting
          fetch(`/api/tracked-trades/summary?period=all`).catch(() => null),
        ]);
        const data = await tradesRes.json();
        const all: any[] = data.trades ?? data.data ?? [];
        setOpenTrades(all.filter((t) => ["active", "tp1_hit"].includes(t.status)));
        setHistory(all.filter((t) => !["active", "tp1_hit"].includes(t.status)).map(toClosed));
        setAllTrades(all as TrackedTrade[]);
        if (summaryRes && summaryRes.ok) {
          const sum = await summaryRes.json();
          if (sum.bySource) setRecord(sum.bySource);
          if (sum.autoByBtc) setByBtc(sum.autoByBtc);
        }
        if (heatRes && heatRes.ok) {
          const heatData = await heatRes.json();
          setPortfolioHeat(heatData.heat ?? heatData.data?.heat ?? 0);
        }
        setLoadError(false);
      } catch {
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [retryCount]);

  const closeTrade = async (signalId: string, exitPrice: number, exitReason: string) => {
    setClosing(signalId);
    try {
      const res = await fetch(`/api/tracked-trades/${signalId}/close`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ exitPrice, exitReason }),
      });
      if (res.ok) {
        setOpenTrades((prev) => prev.filter((t) => t.id !== signalId));
        setCloseForm(null);
        showToast("Trade closed", "success");
        // refresh history so newly closed trade appears immediately
        fetch(`/api/tracked-trades`)
          .then((r) => r.json())
          .then((d) => {
            const all: any[] = d.trades ?? d.data ?? [];
            setHistory(all.filter((t: any) => !["active", "tp1_hit"].includes(t.status)).map(toClosed));
            setAllTrades(all as TrackedTrade[]);
          })
          .catch(() => {});
      } else {
        showToast("Failed to close trade — try again", "error");
      }
    } catch {
      showToast("Failed to close trade — check connection", "error");
    } finally {
      setClosing(null);
    }
  };

  const closingTrade = closeForm ? openTrades.find((t) => t.id === closeForm.tradeId) ?? null : null;
  const shownOpen = bySourceFilter(openTrades, source);
  const shownHistory = bySourceFilter(history, source);
  // Picking a filter shows what it has: no open engine signals but five closed
  // ones opens History, not an empty table.
  const pickSource = (next: SourceFilter) => {
    setSource(next);
    const open = bySourceFilter(openTrades, next).length;
    const closed = bySourceFilter(history, next).length;
    if (tab === "open" && open === 0 && closed > 0) setTab("history");
    else if (tab === "history" && closed === 0 && open > 0) setTab("open");
  };
  const what = source === "auto" ? "signal" : "trade";
  const plural = (n: number) => `${n} ${what}${n === 1 ? "" : "s"}`;

  return (
    <>
      {/* Toast */}
      {toast && (
        <div className="toast-dock md:w-[360px]" role="status">
          <Alert status={toast.type === "success" ? "success" : "danger"} className="shadow-lg">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>{toast.msg}</Alert.Title>
            </Alert.Content>
          </Alert>
        </div>
      )}

      {record && <RecordStrip record={record} byBtc={byBtc} />}

      {/* The 30-day plan: your trades day by day, won or lost, against the milestone. */}
      {!loading && !loadError && (
        <Panel flush>
          <ChallengePanel trades={allTrades} riskUsd={dayRisk.status?.riskPerTradeUsd ?? 0} />
        </Panel>
      )}

      <Panel flush>
        <Tabs
          variant="secondary"
          selectedKey={tab}
          onSelectionChange={(key: Key) => setTab(key as "open" | "history")}
        >
          <div className="flex flex-col gap-3 border-b border-separator px-4 pt-2 sm:flex-row sm:items-end sm:justify-between">
            <Tabs.ListContainer className="bg-transparent">
              <Tabs.List aria-label="Trades" className="text-[13px]">
                <Tabs.Tab id="open">
                  Open
                  <Chip size="sm" variant="soft" className="num ml-1.5">
                    {shownOpen.length}
                  </Chip>
                  <Tabs.Indicator />
                </Tabs.Tab>
                <Tabs.Tab id="history">
                  History
                  {shownHistory.length > 0 && (
                    <Chip size="sm" variant="soft" className="num ml-1.5">
                      {shownHistory.length}
                    </Chip>
                  )}
                  <Tabs.Indicator />
                </Tabs.Tab>
              </Tabs.List>
            </Tabs.ListContainer>
            <div className="flex flex-wrap items-center gap-3 pb-2.5">
              <Segmented aria-label="Whose trades" options={SOURCE_FILTERS} value={source} onChange={pickSource} />
              {tab === "open" && !loading && !loadError && (
                <HeatGauge heat={portfolioHeat} cap={settings.heatCap} />
              )}
            </div>
          </div>

          {loading ? (
            <TableSkeleton />
          ) : loadError ? (
            <div className="p-4">
              <Alert status="danger">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Title>Failed to load trades</Alert.Title>
                  <Alert.Description>Check your connection and try again.</Alert.Description>
                </Alert.Content>
                <Button size="sm" variant="secondary" onPress={() => setRetryCount((c) => c + 1)}>
                  Retry
                </Button>
              </Alert>
            </div>
          ) : (
            <>
              <Tabs.Panel id="open" className="p-0">
                <OpenTab
                  trades={shownOpen}
                  empty={EMPTY_OPEN[source]}
                  emptyAction={shownHistory.length > 0 && (
                    <Button size="sm" variant="secondary" onPress={() => setTab("history")}>
                      See {plural(shownHistory.length)} in History
                    </Button>
                  )}
                  closing={closing}
                  onOpenCloseForm={(id, currentPrice) =>
                    setCloseForm({ tradeId: id, exitPrice: String(currentPrice), reason: "Manual" })
                  }
                  onReview={setReviewing}
                />
              </Tabs.Panel>
              <Tabs.Panel id="history" className="p-0">
                <HistoryTab
                  trades={shownHistory}
                  empty={EMPTY_HISTORY[source]}
                  emptyAction={shownOpen.length > 0 && (
                    <Button size="sm" variant="secondary" onPress={() => setTab("open")}>
                      See {plural(shownOpen.length)} still open
                    </Button>
                  )}
                  onReview={setReviewing}
                />
              </Tabs.Panel>
            </>
          )}
        </Tabs>
      </Panel>

      <ReviewModal trade={reviewing} onClose={() => setReviewing(null)} />

      <CloseTradeModal
        trade={closingTrade}
        closeForm={closeForm}
        closing={closeForm != null && closing === closeForm.tradeId}
        onCloseFormChange={setCloseForm}
        onSubmitClose={closeTrade}
      />
    </>
  );
}

function OpenTab({
  trades,
  empty,
  emptyAction,
  closing,
  onOpenCloseForm,
  onReview,
}: {
  trades: OpenTrade[];
  empty: { title: string; description: string };
  emptyAction?: ReactNode;
  closing: string | null;
  onOpenCloseForm: (id: string, currentPrice: number) => void;
  onReview: (t: { id: string; symbol: string }) => void;
}) {
  return (
    <>
    {/* Phones: one card per trade, every number in view without swiping a table. */}
    <div className="sm:hidden">
      {trades.length === 0 ? (
        <EmptyState icon={Inbox} title={empty.title} description={empty.description} action={emptyAction || undefined} />
      ) : (
        <ul className="divide-y divide-separator">
          {trades.map((trade) => (
            <OpenTradeCard key={trade.id} trade={trade} closing={closing === trade.id} onOpenCloseForm={onOpenCloseForm} onReview={onReview} />
          ))}
        </ul>
      )}
    </div>
    <Table variant="secondary" className="hidden rounded-none sm:block">
      <Table.ScrollContainer className="max-h-[calc(100vh-260px)] overflow-y-auto scrollbar-thin">
        <Table.Content aria-label="Open trades" className="min-w-[860px] text-[13px]">
          <Table.Header className="sticky top-0 z-10 bg-surface">
            <Table.Column isRowHeader>Symbol</Table.Column>
            <Table.Column>Side</Table.Column>
            <Table.Column className="text-right">Entry</Table.Column>
            <Table.Column className="text-right">Mark</Table.Column>
            <Table.Column className="text-right">Stop</Table.Column>
            <Table.Column className="text-right">TP1</Table.Column>
            <Table.Column className="text-right">uP&L ($)</Table.Column>
            <Table.Column className="text-right">Actions</Table.Column>
          </Table.Header>
          <Table.Body
            renderEmptyState={() => (
              <EmptyState icon={Inbox} title={empty.title} description={empty.description} action={emptyAction || undefined} />
            )}
          >
            {trades.map((trade) => (
              <OpenTradeRow
                key={trade.id}
                trade={trade}
                closing={closing === trade.id}
                onOpenCloseForm={onOpenCloseForm}
                onReview={onReview}
              />
            ))}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
    </Table>
    </>
  );
}

/** An open trade marked to the live price: the row and the phone card share it. */
function useOpenTradeLive(trade: OpenTrade) {
  const { price: priceData } = usePriceStream(trade.symbol);
  // usePriceStream returns PriceData | null; extract the numeric price field
  const currentPrice = priceData?.price ?? trade.entry_price;
  const pnl =
    trade.direction === "LONG"
      ? (currentPrice - trade.entry_price) * (trade.margin ?? 1)
      : (trade.entry_price - currentPrice) * (trade.margin ?? 1);
  const pnlPct = ((currentPrice - trade.entry_price) / trade.entry_price) * 100 * (trade.direction === "LONG" ? 1 : -1);
  return { currentPrice, pnl, pnlPct };
}

/** The trade's tags in words: status, engine or practice, half risk, a broken limit. */
function OpenTradeTags({ trade }: { trade: OpenTrade }) {
  const auto = trade.source === "auto";
  return (
    <>
      {trade.status === "tp1_hit" ? "TP1 hit" : "Active"}
      {auto ? " · engine signal (auto)" : trade.mode === "paper" ? " · practice" : ""}
      {isHalf(trade) && <span className="text-warning"> · against BTC, half risk</span>}
      {trade.rule_break && <span className="ml-1 font-semibold text-loss" title={trade.rule_break}>· broke today's limit</span>}
    </>
  );
}

/** An open trade as a phone card: coin and side, live P&L, the four prices, the actions. */
function OpenTradeCard({
  trade,
  closing,
  onOpenCloseForm,
  onReview,
}: {
  trade: OpenTrade;
  closing: boolean;
  onOpenCloseForm: (id: string, price: number) => void;
  onReview: (t: { id: string; symbol: string }) => void;
}) {
  const auto = trade.source === "auto";
  const { currentPrice, pnl, pnlPct } = useOpenTradeLive(trade);
  const [atBreakeven, setAtBreakeven] = useState(false);
  const cells: Array<[string, string]> = [
    ["Entry", fmtPrice(trade.entry_price)],
    ["Mark", fmtPrice(currentPrice)],
    [atBreakeven ? "Stop (BE)" : "Stop", fmtPrice(atBreakeven ? trade.entry_price : trade.stop_loss)],
    ["TP1", fmtPrice(trade.take_profit_1 ?? 0)],
  ];
  return (
    <li className="space-y-2.5 px-4 py-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold">{trade.symbol}</span>
            <DirectionChip direction={trade.direction} />
          </div>
          <div className="mt-0.5 text-2xs text-muted"><OpenTradeTags trade={trade} /></div>
        </div>
        <div className="shrink-0 text-right">
          <div className="font-semibold"><Pnl value={pnl} digits={2} /></div>
          <Pnl value={pnlPct} suffix="%" digits={1} className="text-2xs" />
        </div>
      </div>
      <dl className="grid grid-cols-4 gap-2 text-2xs">
        {cells.map(([k, v]) => (
          <div key={k} className="min-w-0">
            <dt className="text-muted">{k}</dt>
            <dd className="num truncate text-xs text-foreground">{v}</dd>
          </div>
        ))}
      </dl>
      <div className="flex flex-wrap gap-1.5">
        <Button size="sm" variant="secondary" onPress={() => onReview({ id: trade.id, symbol: trade.symbol })}>
          <MessageCircleQuestion className="size-3.5" /> How's it going?
        </Button>
        {!auto && <>
          <Button size="sm" variant="ghost" onPress={() => setAtBreakeven(true)} isDisabled={atBreakeven} aria-label={`Move ${trade.symbol} stop loss to entry price (breakeven)`}>
            <ShieldCheck className="size-3.5" /> SL → Entry
          </Button>
          <Button size="sm" variant="danger" isPending={closing} onPress={() => onOpenCloseForm(trade.id, currentPrice)}>
            <X className="size-3.5" /> Close
          </Button>
        </>}
      </div>
    </li>
  );
}

/** One open trade row — subscribes to its own live price stream. */
function OpenTradeRow({
  trade,
  closing,
  onOpenCloseForm,
  onReview,
}: {
  trade: OpenTrade;
  closing: boolean;
  onOpenCloseForm: (id: string, price: number) => void;
  onReview: (t: { id: string; symbol: string }) => void;
}) {
  const auto = trade.source === "auto";
  const { currentPrice, pnl, pnlPct } = useOpenTradeLive(trade);
  const [atBreakeven, setAtBreakeven] = useState(false);

  return (
    <Table.Row id={trade.id}>
      <Table.Cell>
        <div className="font-semibold">{trade.symbol}</div>
        <div className="text-2xs text-muted"><OpenTradeTags trade={trade} /></div>
      </Table.Cell>
      <Table.Cell>
        <DirectionChip direction={trade.direction} />
      </Table.Cell>
      <Table.Cell className="num text-right">{fmtPrice(trade.entry_price)}</Table.Cell>
      <Table.Cell className="num text-right">{fmtPrice(currentPrice)}</Table.Cell>
      <Table.Cell className="text-right">
        <div className="num">{fmtPrice(atBreakeven ? trade.entry_price : trade.stop_loss)}</div>
        {atBreakeven && (
          <Chip size="sm" variant="soft" className="mt-0.5">
            Breakeven
          </Chip>
        )}
      </Table.Cell>
      <Table.Cell className="num text-right">{fmtPrice(trade.take_profit_1 ?? 0)}</Table.Cell>
      <Table.Cell className="text-right">
        <div className="font-semibold">
          <Pnl value={pnl} digits={2} />
        </div>
        <Pnl value={pnlPct} suffix="%" digits={1} className="text-2xs" />
      </Table.Cell>
      <Table.Cell className="text-right">
        <div className="flex justify-end gap-1.5">
          <Button size="sm" variant="ghost" onPress={() => onReview({ id: trade.id, symbol: trade.symbol })}>
            <MessageCircleQuestion className="size-3.5" />
            How's it going?
          </Button>
          {/* An auto-tracked signal follows the signal's own record: nothing to close by hand. */}
          {!auto && <>
          <Button
            size="sm"
            variant="ghost"
            onPress={() => setAtBreakeven(true)}
            isDisabled={atBreakeven}
            aria-label={`Move ${trade.symbol} stop loss to entry price (breakeven)`}
          >
            <ShieldCheck className="size-3.5" />
            SL → Entry
          </Button>
          <Button
            size="sm"
            variant="danger"
            isPending={closing}
            onPress={() => onOpenCloseForm(trade.id, currentPrice)}
          >
            <X className="size-3.5" />
            Close
          </Button>
          </>}
        </div>
      </Table.Cell>
    </Table.Row>
  );
}

function CloseTradeModal({
  trade,
  closeForm,
  closing,
  onCloseFormChange,
  onSubmitClose,
}: {
  trade: OpenTrade | null;
  closeForm: CloseForm | null;
  closing: boolean;
  onCloseFormChange: (form: CloseForm | null) => void;
  onSubmitClose: (id: string, price: number, reason: string) => void;
}) {
  const exitNum = Number(closeForm?.exitPrice);
  const invalid = !exitNum || exitNum <= 0;

  const submit = () => {
    const price = Number(closeForm?.exitPrice);
    if (!closeForm || !price || price <= 0) return;
    onSubmitClose(closeForm.tradeId, price, closeForm.reason);
  };

  return (
    <Modal.Backdrop
      isOpen={closeForm != null}
      onOpenChange={(open) => {
        if (!open) onCloseFormChange(null);
      }}
    >
      <Modal.Container size="sm">
        <Modal.Dialog>
          <Modal.CloseTrigger />
          <Modal.Header>
            <Modal.Heading>Close {trade?.symbol ?? "trade"}</Modal.Heading>
            {trade && (
              <div className="mt-1 flex items-center gap-2 text-xs text-muted">
                <DirectionChip direction={trade.direction} />
                <span>
                  Entry <span className="num text-foreground">{fmtPrice(trade.entry_price)}</span>
                </span>
              </div>
            )}
          </Modal.Header>
          <Modal.Body className="space-y-4">
            <NumberField
              value={closeForm && closeForm.exitPrice !== "" ? exitNum : NaN}
              onChange={(v) =>
                onCloseFormChange(
                  closeForm ? { ...closeForm, exitPrice: Number.isNaN(v) ? "" : String(v) } : null
                )
              }
              minValue={0}
              formatOptions={{ maximumFractionDigits: 8, useGrouping: false }}
              isInvalid={invalid}
              fullWidth
            >
              <Label>Exit price</Label>
              <NumberField.Group>
                <NumberField.Input className="num" />
              </NumberField.Group>
            </NumberField>
            <Select
              value={closeForm?.reason ?? "Manual"}
              onChange={(value) => {
                if (value == null || Array.isArray(value)) return;
                onCloseFormChange(closeForm ? { ...closeForm, reason: String(value) } : null);
              }}
              fullWidth
            >
              <Label>Exit reason</Label>
              <Select.Trigger>
                <Select.Value />
                <Select.Indicator />
              </Select.Trigger>
              <Select.Popover>
                <ListBox>
                  {CLOSE_REASONS.map((r) => (
                    <ListBox.Item key={r} id={r} textValue={r}>
                      {r}
                      <ListBox.ItemIndicator />
                    </ListBox.Item>
                  ))}
                </ListBox>
              </Select.Popover>
            </Select>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onPress={() => onCloseFormChange(null)}>
              Cancel
            </Button>
            <Button variant="danger" isPending={closing} isDisabled={closing || invalid} onPress={submit}>
              {closing ? "Closing..." : "Close trade"}
            </Button>
          </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}

function HistoryTab({
  trades,
  empty,
  emptyAction,
  onReview,
}: {
  trades: ClosedTrade[];
  empty: { title: string; description: string };
  emptyAction?: ReactNode;
  onReview: (t: { id: string; symbol: string }) => void;
}) {
  return (
    <>
    {/* Phones: one card per trade, the result first. */}
    <div className="sm:hidden">
      {trades.length === 0 ? (
        <EmptyState icon={History} title={empty.title} description={empty.description} action={emptyAction || undefined} />
      ) : (
        <ul className="divide-y divide-separator">
          {trades.map((trade) => <HistoryCard key={trade.id} trade={trade} onReview={onReview} />)}
        </ul>
      )}
    </div>
    <Table variant="secondary" className="hidden rounded-none sm:block">
      <Table.ScrollContainer className="max-h-[calc(100vh-260px)] overflow-y-auto scrollbar-thin">
        <Table.Content aria-label="Trade history" className="min-w-[900px] text-[13px]">
          <Table.Header className="sticky top-0 z-10 bg-surface">
            <Table.Column isRowHeader>Symbol</Table.Column>
            <Table.Column>Side</Table.Column>
            <Table.Column className="text-right">Entry</Table.Column>
            <Table.Column className="text-right">Exit</Table.Column>
            <Table.Column className="text-right">P&L ($)</Table.Column>
            <Table.Column className="text-right">Result</Table.Column>
            <Table.Column>Why</Table.Column>
            <Table.Column className="text-right">Closed</Table.Column>
          </Table.Header>
          <Table.Body
            renderEmptyState={() => (
              <EmptyState icon={History} title={empty.title} description={empty.description} action={emptyAction || undefined} />
            )}
          >
            {trades.map((trade) => {
              const pnl = trade.pnl ?? 0;
              const entryVal = trade.entryPrice ?? trade.entry ?? 0;
              const exitVal = trade.exitPrice ?? trade.exit ?? 0;
              const dir = trade.direction ?? (trade.side === "long" ? "LONG" : trade.side === "short" ? "SHORT" : trade.side?.toUpperCase() ?? "—");
              const dateStr = trade.closedAt ?? trade.exitDate;
              return (
                <Table.Row key={trade.id} id={trade.id}>
                  <Table.Cell className="font-semibold">
                    {trade.symbol}
                    {trade.source === "auto" ? (
                      <span className="ml-1.5 inline-flex items-center gap-0.5 text-3xs font-normal uppercase tracking-wide text-muted">
                        <Bot className="size-3" aria-hidden /> engine
                      </span>
                    ) : trade.mode === "paper" && (
                      <span className="ml-1.5 text-3xs font-normal uppercase tracking-wide text-muted">practice</span>
                    )}
                    {isHalf(trade) && (
                      <span className="ml-1.5 text-3xs font-semibold uppercase tracking-wide text-warning" title="Against BTC's trend: sized at half the usual risk">half risk</span>
                    )}
                    {trade.rule_break && (
                      <span className="ml-1.5 text-3xs font-semibold uppercase tracking-wide text-loss" title={trade.rule_break}>broke limit</span>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {dir === "LONG" || dir === "SHORT" ? (
                      <DirectionChip direction={dir} />
                    ) : (
                      <span className="text-muted">{dir}</span>
                    )}
                  </Table.Cell>
                  <Table.Cell className="num text-right">{fmtPrice(entryVal)}</Table.Cell>
                  <Table.Cell className={cn("num text-right", !exitVal && "text-muted")}>
                    {exitVal ? fmtPrice(exitVal) : "—"}
                  </Table.Cell>
                  <Table.Cell className="text-right font-semibold">
                    {pnl !== 0 ? <Pnl value={pnl} digits={2} /> : <span className="num text-muted">—</span>}
                  </Table.Cell>
                  <Table.Cell className={cn("num text-right", trade.r != null && (trade.r > 0.05 ? "text-profit" : trade.r < -0.05 ? "text-loss" : ""))}>
                    {trade.r != null ? formatR(trade.r) : <span className="text-muted">—</span>}
                  </Table.Cell>
                  <Table.Cell className="max-w-[320px]">
                    <button
                      type="button"
                      onClick={() => onReview({ id: trade.id, symbol: trade.symbol })}
                      className="flex w-full items-center gap-1.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-focus"
                    >
                      <span className="min-w-0 flex-1 truncate">
                        {trade.review ? trade.review.headline : <span className="text-muted">See why</span>}
                      </span>
                      <span className="shrink-0 text-xs text-brand-text underline underline-offset-2">details</span>
                    </button>
                  </Table.Cell>
                  <Table.Cell className="num text-right text-muted">
                    {dateStr ? new Date(dateStr).toLocaleDateString() : "—"}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
    </Table>
    </>
  );
}

/** A closed trade as a phone card: the coin, the result in R and dollars, the prices, and why. */
function HistoryCard({ trade, onReview }: { trade: ClosedTrade; onReview: (t: { id: string; symbol: string }) => void }) {
  const pnl = trade.pnl ?? 0;
  const entryVal = trade.entryPrice ?? trade.entry ?? 0;
  const exitVal = trade.exitPrice ?? trade.exit ?? 0;
  const dir = trade.direction ?? (trade.side === "long" ? "LONG" : trade.side === "short" ? "SHORT" : null);
  const dateStr = trade.closedAt ?? trade.exitDate;
  const r = trade.r;
  return (
    <li className="space-y-1.5 px-4 py-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-semibold">{trade.symbol}</span>
            {dir && <DirectionChip direction={dir} />}
          </div>
          <div className="mt-0.5 text-3xs uppercase tracking-wide text-muted">
            {trade.source === "auto" ? "engine" : trade.mode === "paper" ? "practice" : "real"}
            {isHalf(trade) && <span className="font-semibold text-warning"> · half risk</span>}
            {trade.rule_break && <span className="font-semibold text-loss" title={trade.rule_break}> · broke limit</span>}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className={cn("num text-base font-semibold", r != null && (r > 0.05 ? "text-profit" : r < -0.05 ? "text-loss" : ""))}>
            {r != null ? formatR(r) : <span className="text-muted">—</span>}
          </div>
          {pnl !== 0 && <Pnl value={pnl} digits={2} className="text-2xs" />}
        </div>
      </div>
      <div className="num flex justify-between gap-3 text-2xs text-muted">
        <span>{fmtPrice(entryVal)} → {exitVal ? fmtPrice(exitVal) : "—"}</span>
        <span>{dateStr ? new Date(dateStr).toLocaleDateString() : ""}</span>
      </div>
      <button
        type="button"
        onClick={() => onReview({ id: trade.id, symbol: trade.symbol })}
        className="flex w-full items-center gap-1.5 text-left text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-focus"
      >
        <span className="min-w-0 flex-1 truncate">{trade.review ? trade.review.headline : <span className="text-muted">See why it won or lost</span>}</span>
        <span className="shrink-0 text-xs text-brand-text underline underline-offset-2">details</span>
      </button>
    </li>
  );
}

/** Your record next to the engine's: win rate and net R, all time. */
function RecordStrip({ record, byBtc }: { record: Record<TradeSource, TradeStats>; byBtc: ByBtc | null }) {
  const card = (title: string, hint: string, st: TradeStats) => {
    const closed = st.won + st.lost + st.even;
    return (
      <div className="rounded-xl border border-border bg-surface px-4 py-3">
        <div className="text-2xs font-medium uppercase tracking-wide text-muted">{title}</div>
        <div className="mt-1 flex flex-wrap items-baseline gap-x-3">
          <span className="num text-xl font-semibold">{st.winRate == null ? "—" : `${Math.round(st.winRate)}%`}</span>
          <span className="text-xs text-muted">win rate</span>
          {closed > 0 && <span className={cn("num text-sm font-semibold", st.netR > 0.05 ? "text-profit" : st.netR < -0.05 ? "text-loss" : "")}>{formatR(st.netR)}</span>}
        </div>
        <div className="mt-0.5 text-xs text-muted">
          {st.won} won · {st.lost} lost{st.even > 0 ? ` · ${st.even} even` : ""}{st.open > 0 ? ` · ${st.open} open` : ""}
          {closed < 20 && closed > 0 ? " · under 20 closed: mostly luck so far" : ""}
        </div>
        <div className="mt-1 text-2xs text-muted">{hint}</div>
      </div>
    );
  };
  return (
    <div className="mb-4 grid gap-3 sm:grid-cols-2">
      {card("Engine signals (auto)", "Every BUY/SELL the engine sent, tracked automatically as practice. No real orders.", record.auto)}
      {card("Your trades", "Trades you logged with Buy or Sell, real and practice.", record.manual)}
      {byBtc && byBtc.against.taken > 0 && (
        <p className="text-xs text-muted sm:col-span-2">
          Engine signals with BTC: {btcLine(byBtc.with)}. Against BTC, at half risk: {btcLine(byBtc.against)}.
          {" "}If the against-BTC ones keep losing after 20 or more have closed, they should be switched off.
        </p>
      )}
    </div>
  );
}

/** "3 closed, 67% won, +1.4R" (R in your usual risk: a half-size trade counts half). */
function btcLine(st: TradeStats): string {
  const closed = st.won + st.lost + st.even;
  if (closed === 0) return st.open > 0 ? `${st.open} open, none closed yet` : "none yet";
  return `${closed} closed, ${Math.round(st.winRate ?? 0)}% won, ${formatR(st.netR)}`;
}

function ReviewModal({ trade, onClose }: { trade: { id: string; symbol: string } | null; onClose: () => void }) {
  return (
    <Modal.Backdrop isOpen={trade != null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <Modal.Container size="md">
        <Modal.Dialog>
          <Modal.CloseTrigger />
          <Modal.Header>
            <Modal.Heading>Why? · {trade?.symbol.replace("USDT", "")}</Modal.Heading>
          </Modal.Header>
          <Modal.Body>{trade && <TradeReviewLoader id={trade.id} />}</Modal.Body>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}

function TableSkeleton() {
  return (
    <div className="space-y-2 p-4" aria-busy="true" aria-label="Loading trades">
      <Skeleton className="h-4 w-full rounded" />
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-full rounded" />
      ))}
    </div>
  );
}
